// Session gate, app-wide refusal states and the route switch. Access is the
// api's decision: 401 returns to sign-in, 403 not_staff replaces every page
// with the refusal.
import { useEffect, useState } from 'react';
import { BrandMark, Field, Spinner, ErrorNote, EmptyState } from './ui/primitives.js';
import type { ApiError } from './api/client.js';
import { COPY } from './copy.js';
import { HomePage } from './pages/Home.js';
import { MeetingPage } from './pages/Meeting.js';
import { RoomPage } from './pages/Room.js';
import { Link, paths, useRoute } from './router.js';
import { useServices } from './services.js';

type Gate =
  | { kind: 'checking' }
  | { kind: 'signed_out'; reason: 'none' | 'expired' }
  | { kind: 'signed_in' }
  | { kind: 'refused' };

export function App() {
  const { auth, authFailures } = useServices();
  const [gate, setGate] = useState<Gate>({ kind: 'checking' });

  useEffect(
    () =>
      auth.onChange((signedIn) =>
        setGate((prev) => {
          if (signedIn) return prev.kind === 'refused' ? prev : { kind: 'signed_in' };
          if (prev.kind === 'signed_out') return prev;
          return { kind: 'signed_out', reason: 'none' };
        }),
      ),
    [auth],
  );

  useEffect(
    () =>
      authFailures.subscribe((err: ApiError) =>
        setGate(err.code === 'not_staff' ? { kind: 'refused' } : { kind: 'signed_out', reason: 'expired' }),
      ),
    [authFailures],
  );

  const signOut = () => {
    void auth.signOut().finally(() => setGate({ kind: 'signed_out', reason: 'none' }));
  };

  return (
    <div className="app">
      <header className="topbar">
        <Link href={paths.home()} className="brand">
          <BrandMark /><span>{COPY.appName}</span>
        </Link>
        {(gate.kind === 'signed_in' || gate.kind === 'refused') && (
          <button type="button" onClick={signOut}>
            {COPY.signOut}
          </button>
        )}
      </header>
      <main>
        <Body gate={gate} />
      </main>
    </div>
  );
}

function Body({ gate }: { gate: Gate }) {
  switch (gate.kind) {
    case 'checking':
      return <div className="page loading"><Spinner /><span>{COPY.loading}</span></div>;
    case 'refused':
      return (
        <div className="page refusal" data-testid="refusal">
          <h1>{COPY.refusedTitle}</h1>
          <p>{COPY.refusedBody}</p>
        </div>
      );
    case 'signed_out':
      return <SignIn expired={gate.reason === 'expired'} />;
    case 'signed_in':
      return <Routes />;
  }
}

function SignIn({ expired }: { expired: boolean }) {
  const { auth } = useServices();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit() {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await auth.signInWithPassword(email.trim(), password);
    } catch (cause) {
      const invalid = typeof cause === 'object' && cause !== null &&
        'code' in cause && cause.code === 'invalid_credentials';
      setError(invalid ? COPY.invalidCredentials : COPY.signInFailed);
    } finally {
      setPassword('');
      setPending(false);
    }
  }

  return (
    <div className="page signin" data-testid="signin">
      <BrandMark className="brand-mark--lg" /><h1>{COPY.signInTitle}</h1>
      {expired && <p className="warning">{COPY.sessionExpired}</p>}
      {error && <ErrorNote message={error} />}
      <p>{COPY.signInBody}</p>
      <form onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <Field label={COPY.email}>
        <input id="signin-email" name="email" type="email" autoComplete="username"
          required value={email} disabled={pending} onChange={(event) => setEmail(event.target.value)} /></Field>
        <Field label={COPY.password}>
        <input id="signin-password" name="password" type="password" autoComplete="current-password"
          required value={password} disabled={pending} onChange={(event) => setPassword(event.target.value)} /></Field>
        <button type="submit" className="button primary" disabled={pending}>{COPY.signInButton}</button>
      </form>
    </div>
  );
}

function Routes() {
  const route = useRoute();
  switch (route.name) {
    case 'home':
      return <HomePage />;
    case 'room':
      return <RoomPage key={route.slug} slug={route.slug} />;
    case 'meeting':
      return <MeetingPage key={route.id} id={route.id} atMs={route.atMs} />;
    case 'not_found':
      return (
        <div className="page">
          <EmptyState title={COPY.notFound} />
          <Link href={paths.home()}>{COPY.backHome}</Link>
        </div>
      );
  }
}
